import { createTheme, ThemeOptions } from '@mui/material/styles';

/**
 * M.E.-Kollektiv theme, built from the corporate identity document.
 *
 * Colours (CI):
 *   Blau         #5D74AB   Hellblau  #A7B7D6
 *   Off-white    #E3E6EC   Anthrazit #353535
 *
 * One blue, everywhere the site draws itself: #5D74AB is a tile's edge, a
 * button, a link inside a paragraph. The lighter CI blue used to carry the
 * fills while a darkened shade carried the text, and the page ended up showing
 * three blues at once - so the two were collapsed into the one the identity
 * names.
 *
 * The artwork is the exception and stays as it was exported: the wordmark in
 * the header, the mark in the browser tab and the share card all carry the
 * identity's lighter #728FCE, baked into the pixels. That is deliberate, not an
 * oversight - it is a decision for whoever draws the logo, not for the palette.
 * Nothing here should be "corrected" to match them.
 *
 * It carries text as well as fills because it can: #5D74AB reaches ~4.6:1
 * against white, past the 4.5:1 that body-sized text needs. That is the number
 * to check before this value is ever changed again - a lighter blue would mean
 * going back to a separate shade for text.
 *
 * `light` and `accentTextDark` are the dark view's counterparts and stay pale:
 * the same blue on a dark background comes to ~3.6:1, which is enough for a
 * border and not for a line of text. The dark view is switched off in
 * content/display.json at the moment; the palette stays correct for the day it
 * is turned on.
 *
 * `dark` is not a second blue in the design but a state: it is what a filled
 * button turns under the pointer, and it exists so that hovering changes the
 * colour rather than moving anything.
 */

export const BRAND = {
  primary: '#5D74AB',
  light: '#A7B7D6',
  /** A step darker, for hover and pressed states only. */
  dark: '#47598C',
  offWhite: '#E3E6EC',
  anthracite: '#353535',
  /** Blue for body-sized text and links on light backgrounds: the same blue. */
  accentText: '#5D74AB',
  /** Its counterpart on dark backgrounds, where that blue is too dark to read. */
  accentTextDark: '#A7B7D6',
} as const;

/**
 * One family throughout: Montserrat, exactly as the CI prescribes for running
 * text. The display headings used to carry Cormorant Garamond as a free
 * stand-in for the CI display serif, The Seasons; they now separate themselves
 * by weight and tracking instead of by family. The Seasons is licensable for
 * web (My Creative Land, yearly subscription by pageview tier; also on Adobe
 * Fonts web projects) — it was simply never bought for this project.
 */
const BODY_FONT = '"Montserrat", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", sans-serif';
const DISPLAY_FONT = BODY_FONT;

export const FONTS = { display: DISPLAY_FONT, body: BODY_FONT } as const;

const baseThemeOptions: ThemeOptions = {
  palette: {
    primary: {
      main: BRAND.primary,
      light: BRAND.light,
      dark: BRAND.accentText,
    },
    secondary: {
      main: BRAND.primary,
      light: BRAND.light,
      dark: '#3C4E7D',
    },
    success: { main: '#2E7D5B' },
    error: { main: '#B3261E' },
    warning: { main: '#9A6700' },
    info: { main: BRAND.primary },
    grey: {
      50: '#F7F8FA',
      100: BRAND.offWhite,
      200: '#D3D8E2',
      300: '#B9C0CE',
      400: '#8D95A6',
      500: '#6B7280',
      600: '#525A67',
      700: '#41474F',
      800: BRAND.anthracite,
      900: '#232323',
    },
  },
  typography: {
    fontFamily: BODY_FONT,
    // Display headings (h1-h3) run in Montserrat Bold with slightly negative
    // tracking, which large sans headings need to stay tight; the smaller
    // headings stay on Semi-Bold, as the CI prescribes for running text.
    h1: {
      fontFamily: DISPLAY_FONT,
      fontSize: 'clamp(2.25rem, 5vw, 3.5rem)',
      fontWeight: 700,
      lineHeight: 1.15,
      letterSpacing: '-0.015em',
    },
    // h2 and h3 sit below the sizes a display scale would give them, and for a
    // reason that is easy to lose: no page on this site uses the h1 above at
    // its full size, because a headline that fills the screen is the thing a
    // visitor has to read past before the page starts. Left at a display h2,
    // every section heading came out larger than the heading of the page it sat
    // on. So the scale is set against what the pages actually render, not
    // against the theme's own h1, and the whole of it is calibrated at the top
    // of the range rather than the bottom - on a desktop window the earlier
    // scale read as a phone layout. Ceilings, largest first:
    //   3.25  hero h1 (HomePage)   2.875 page h1 (PageHeader)
    //   2.625 h2, section          1.9375 h3, sub-section
    //   1.625 h4, card title       1.1875 body1
    // Each step is far enough from the last to be seen at a glance; change one
    // and check it against its neighbours here.
    h2: {
      fontFamily: DISPLAY_FONT,
      fontSize: 'clamp(1.875rem, 3.8vw, 2.625rem)',
      fontWeight: 700,
      lineHeight: 1.22,
      letterSpacing: '-0.015em',
    },
    h3: {
      fontFamily: DISPLAY_FONT,
      fontSize: 'clamp(1.4375rem, 2.9vw, 1.9375rem)',
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontFamily: BODY_FONT,
      fontSize: 'clamp(1.25rem, 2.5vw, 1.625rem)',
      fontWeight: 600,
      lineHeight: 1.35,
    },
    h5: {
      fontFamily: BODY_FONT,
      fontSize: 'clamp(1.0625rem, 2vw, 1.25rem)',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h6: {
      fontFamily: BODY_FONT,
      fontSize: 'clamp(1rem, 1.8vw, 1.125rem)',
      fontWeight: 600,
      lineHeight: 1.45,
    },
    // Generous line height throughout: much of the audience reads with
    // cognitive symptoms and visual sensitivity.
    body1: {
      fontSize: 'clamp(1.0625rem, 1.5vw, 1.1875rem)',
      lineHeight: 1.8,
      fontWeight: 400,
    },
    body2: {
      fontSize: 'clamp(0.9375rem, 1.3vw, 1.0625rem)',
      lineHeight: 1.75,
      fontWeight: 400,
    },
    button: {
      fontFamily: BODY_FONT,
      fontSize: '1rem',
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: '0.01em',
    },
    overline: {
      fontFamily: BODY_FONT,
      fontWeight: 600,
      fontSize: '0.8125rem',
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // Respect the OS setting - vestibular symptoms are common with ME.
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            transitionDuration: '0.01ms !important',
            scrollBehavior: 'auto !important',
          },
        },
        ':focus-visible': {
          outline: `3px solid ${BRAND.primary}`,
          outlineOffset: '2px',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 999,
          padding: '12px 28px',
          fontWeight: 600,
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: 16, boxShadow: 'none' },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: 16, backgroundImage: 'none' },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: { textUnderlineOffset: '0.2em' },
      },
    },
    MuiContainer: {
      styleOverrides: {
        // Wider gutters once there is a window to spare, so that a page filling
        // a desktop screen still has a margin rather than running to the glass.
        root: {
          paddingLeft: 24,
          paddingRight: 24,
          '@media (min-width:900px)': { paddingLeft: 48, paddingRight: 48 },
        },
        // MUI stops `xl` at 1536px. Past that the page would have held still
        // and grown margins instead, which is the look this width exists to
        // get away from, so it keeps going to a point where a line finally is
        // too long to track however generous the leading.
        maxWidthXl: {
          '@media (min-width:1536px)': { maxWidth: 1720 },
        },
      },
    },
  },
  shape: { borderRadius: 12 },
};

const lightThemeOptions: ThemeOptions = {
  ...baseThemeOptions,
  palette: {
    ...baseThemeOptions.palette,
    mode: 'light',
    background: {
      default: '#FFFFFF',
      paper: '#FFFFFF',
    },
    text: {
      primary: BRAND.anthracite,
      secondary: '#525A67',
    },
    divider: 'rgba(53, 53, 53, 0.14)',
  },
};

const darkThemeOptions: ThemeOptions = {
  ...baseThemeOptions,
  palette: {
    ...baseThemeOptions.palette,
    mode: 'dark',
    primary: {
      main: BRAND.light,
      light: '#C3CEE4',
      dark: BRAND.primary,
    },
    secondary: {
      main: BRAND.primary,
      light: BRAND.light,
      dark: BRAND.dark,
    },
    background: {
      default: '#1B1F27',
      paper: '#242933',
    },
    text: {
      primary: BRAND.offWhite,
      secondary: '#B4BCCB',
    },
    divider: 'rgba(227, 230, 236, 0.16)',
  },
};

export const createAppTheme = (mode: 'light' | 'dark') =>
  createTheme(mode === 'dark' ? darkThemeOptions : lightThemeOptions);

export const theme = createTheme(lightThemeOptions);
